if (!customElements.get('box-builder')) {
  customElements.define(
    'box-builder',
    class BoxBuilder extends HTMLElement {
      constructor() {
        super();

        this.unitsPerBox = parseInt(this.dataset.unitsPerBox, 10) || 25;
        this.maxBoxes = parseInt(this.dataset.maxBoxes, 10) || 4;
        this.currency = this.dataset.currency || 'COP';
        this.unitSingular = this.cleanLabel(this.dataset.unitSingular, 'unidad');
        this.unitPlural = this.cleanLabel(this.dataset.unitPlural, 'unidades');
        this.boxImage = this.dataset.boxImage || '';
        this.tiers = JSON.parse(this.dataset.tiers || '[]');
        this.tiers.sort((a, b) => a.minBoxes - b.minBoxes);

        this.pendingBoxCount = 0;
        this.selectedBoxCount = 0;
        this.activeBoxIndex = 0;
        this.boxes = [];
        this.simpleMode = true;

        this.stepSelectPanel = this.querySelector('[data-panel="select"]');
        this.stepBuildPanel = this.querySelector('[data-panel="build"]');
        this.stepElements = this.querySelectorAll('.box-builder__step');

        this.boxTabsContainer = this.querySelector('.box-builder__box-tabs');
        this.buildHeaderEl = this.querySelector('.box-builder__build-header');
        this.progressFill = this.querySelector('.box-builder__progress-fill');
        this.progressText = this.querySelector('.box-builder__progress-text');
        this.progressCount = this.querySelector('.box-builder__progress-count');
        this.progressPct = this.querySelector('.box-builder__progress-pct');
        this.progressHint = this.querySelector('.box-builder__progress-hint');
        this.progressTrack = this.querySelector('.box-builder__progress-track');
        this.selectionOverviewEl = this.querySelector('.box-builder__selection-overview');
        this.summaryEl = this.querySelector('.box-builder__summary');
        this.addButton = this.querySelector('.box-builder__add-btn');
        this.addButtonText = this.querySelector('.box-builder__add-btn-text');
        this.stickyTotalLabel = this.querySelector('[data-sticky-total-label]');
        this.stickyTotalMeta = this.querySelector('[data-sticky-total-meta]');
        this.stickyTotalValue = this.querySelector('[data-sticky-total-value]');
        this.spinner = this.querySelector('.loading__spinner');
        this.errorEl = this.querySelector('.box-builder__error');
        this.continueButton = this.querySelector('.box-builder__continue');
        this.selectionSummaryEl = this.querySelector('[data-selection-summary]');
        this.selectedBoxesPill = this.querySelector('[data-selected-boxes-pill]');
        this.unitPricePill = this.querySelector('[data-unit-price-pill]');
        this.totalUnitsPill = this.querySelector('[data-total-units-pill]');
        this.clearBoxButton = this.querySelector('.box-builder__clear-box');
        this.flavorRows = Array.from(this.querySelectorAll('.box-builder__flavor'));
        this.flavorOrderIndex = new Map(
          this.flavorRows.map((row, index) => [String(row.dataset.variantId || ''), index])
        );
        this.flavorMeta = new Map(
          this.flavorRows.map((row) => {
            const maxAvailable = row.dataset.maxAvailable === '' ? null : parseInt(row.dataset.maxAvailable, 10);
            return [
              row.dataset.variantId,
              {
                title: row.dataset.productTitle || 'este sabor',
                inventoryLimited: row.dataset.inventoryLimited === 'true',
                maxAvailable: Number.isNaN(maxAvailable) ? null : maxAvailable,
              },
            ];
          })
        );

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindEvents();
        this.updateSelectionStep();
        this.showStep(1);
      }

      cleanLabel(value, fallback) {
        const label = String(value || '').trim();
        const clean = label.replace(/[<>]/g, '').trim();
        return clean || fallback;
      }

      getUnitName(count) {
        return Number(count) === 1 ? this.unitSingular : this.unitPlural;
      }

      formatUnitCount(count) {
        return `${count} ${this.getUnitName(count)}`;
      }

      getBoxName(count) {
        return Number(count) === 1 ? 'caja' : 'cajas';
      }

      formatBoxCount(count) {
        return `${count} ${this.getBoxName(count)}`;
      }

      getOwnedBoxCount(count) {
        return Number(count) === 1 ? `tu ${this.getBoxName(count)}` : `tus ${this.formatBoxCount(count)}`;
      }

      capitalizeLabel(value) {
        return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';
      }

      getBundleDisplayName(count = 1) {
        if ((this.unitPlural || '').toLowerCase() === 'unidades') {
          return Number(count) === 1 ? 'Caja personalizada' : 'Cajas personalizadas';
        }

        return `${this.capitalizeLabel(this.getBoxName(count))} de ${this.unitPlural}`;
      }

      getTierForBoxCount(count) {
        let tier = this.tiers[0] || null;
        for (const currentTier of this.tiers) {
          if (count >= currentTier.minBoxes) tier = currentTier;
        }
        return tier;
      }

      get currentTier() {
        return this.getTierForBoxCount(this.selectedBoxCount);
      }

      get totalUnitsAllBoxes() {
        return this.boxes.reduce((sum, box) => {
          return sum + Object.values(box).reduce((subtotal, quantity) => subtotal + quantity, 0);
        }, 0);
      }

      getSimplePool() {
        return this.boxes[0] || {};
      }

      getWorkingUnitsTotal() {
        return this.simpleMode ? this.getUnitsForBox(this.getSimplePool()) : this.totalUnitsAllBoxes;
      }

      get requiredUnitsAllBoxes() {
        return this.selectedBoxCount * this.unitsPerBox;
      }

      get allBoxesComplete() {
        if (this.simpleMode) {
          return this.boxes.length > 0 && this.getWorkingUnitsTotal() === this.requiredUnitsAllBoxes;
        }
        return this.boxes.length > 0 && this.boxes.every((box) => this.getUnitsForBox(box) === this.unitsPerBox);
      }

      getUnitsForBox(box) {
        if (!box) return 0;
        return Object.values(box).reduce((sum, quantity) => sum + quantity, 0);
      }

      getFlavorSortIndex(variantId) {
        const index = this.flavorOrderIndex.get(String(variantId));
        return Number.isInteger(index) ? index : Number.MAX_SAFE_INTEGER;
      }

      getBoxImageStorage() {
        try {
          const parsed = JSON.parse(localStorage.getItem('box_builder_images') || '{}');
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (error) {
          return {};
        }
      }

      persistBoxImages(imageMap) {
        if (!imageMap || Object.keys(imageMap).length === 0) return;

        const current = this.getBoxImageStorage();
        const merged = { ...current, ...imageMap };
        const keys = Object.keys(merged).sort();
        const trimmedKeys = keys.slice(-250);
        const trimmed = {};

        trimmedKeys.forEach((key) => {
          trimmed[key] = merged[key];
        });

        try {
          localStorage.setItem('box_builder_images', JSON.stringify(trimmed));
        } catch (error) {
          // Ignore storage quota errors.
        }
      }

      applyBoxImagesInDom(imageMap = null) {
        const resolvedMap = imageMap || this.getBoxImageStorage();
        if (!resolvedMap || Object.keys(resolvedMap).length === 0) return;

        const imageTargets = document.querySelectorAll('[data-box-image-target][data-box-id]');

        imageTargets.forEach((imageEl) => {
          const boxId = imageEl.getAttribute('data-box-id');
          const imageUrl = boxId ? resolvedMap[boxId] : null;

          if (!imageUrl) return;

          imageEl.setAttribute('src', imageUrl);
          imageEl.removeAttribute('hidden');

          const mediaWrapper = imageEl.closest('.box-group-card__media');
          const placeholder = mediaWrapper?.querySelector('[data-box-image-placeholder]');
          if (placeholder) {
            placeholder.style.display = 'none';
          }
        });
      }

      getBoxUnits(index) {
        return this.getUnitsForBox(this.boxes[index]);
      }

      isBoxComplete(index) {
        return this.getBoxUnits(index) === this.unitsPerBox;
      }

      getTotalSelectedForVariant(variantId) {
        if (this.simpleMode) {
          return this.getSimplePool()[variantId] || 0;
        }

        return this.boxes.reduce((sum, box) => sum + (box[variantId] || 0), 0);
      }

      getVariantMeta(variantId) {
        return this.flavorMeta.get(String(variantId)) || { title: 'este sabor', inventoryLimited: false, maxAvailable: null };
      }

      getFlavorTotals(boxIndex = null) {
        const totals = new Map();
        const boxes = Number.isInteger(boxIndex) ? [this.boxes[boxIndex] || {}] : this.boxes;

        boxes.forEach((box) => {
          Object.entries(box || {}).forEach(([variantId, quantity]) => {
            if (!quantity) return;
            totals.set(variantId, (totals.get(variantId) || 0) + quantity);
          });
        });

        return Array.from(totals.entries())
          .map(([variantId, quantity]) => ({
            variantId,
            quantity,
            title: this.getVariantMeta(variantId).title,
          }))
          .sort((left, right) => {
            if (right.quantity !== left.quantity) return right.quantity - left.quantity;
            return left.title.localeCompare(right.title, document.documentElement.lang || 'es');
          });
      }

      getVariantMaxAvailable(variantId) {
        const { inventoryLimited, maxAvailable } = this.getVariantMeta(variantId);
        if (!inventoryLimited) return null;
        return Number.isInteger(maxAvailable) ? maxAvailable : null;
      }

      getRemainingVariantAvailability(variantId) {
        const maxAvailable = this.getVariantMaxAvailable(variantId);
        if (maxAvailable === null) return Number.POSITIVE_INFINITY;
        return Math.max(maxAvailable - this.getTotalSelectedForVariant(variantId), 0);
      }

      getVariantAvailabilityMessage(variantId) {
        const { title, maxAvailable } = this.getVariantMeta(variantId);
        if (maxAvailable === null) return '';

        const remaining = this.getRemainingVariantAvailability(variantId);
        if (remaining <= 0) return `No hay mas ${this.unitPlural} disponibles de ${title}.`;
        if (remaining === 1) return `Solo queda 1 ${this.unitSingular} disponible de ${title}.`;
        return `Solo quedan ${this.formatUnitCount(remaining)} disponibles de ${title}.`;
      }

      validateInventoryAvailability() {
        for (const [variantId, meta] of this.flavorMeta.entries()) {
          if (meta.maxAvailable === null) continue;
          const selected = this.getTotalSelectedForVariant(variantId);
          if (selected > meta.maxAvailable) {
            this.showError(`Seleccionaste ${this.formatUnitCount(selected)} de ${meta.title}, pero solo hay ${this.formatUnitCount(meta.maxAvailable)} disponibles.`);
            return false;
          }
        }

        return true;
      }

      isSoldOutError(data) {
        const msg = (data?.description || data?.message || '').toLowerCase();
        return msg.includes('sold out') || msg.includes('not enough') || msg.includes('agotado');
      }

      extractSoldOutProductName(data) {
        const msg = data?.description || data?.message || '';
        const match = msg.match(/product\s+'([^']+)'/i) || msg.match(/producto\s+'([^']+)'/i);
        return match ? match[1] : null;
      }

      findVariantByProductName(productName) {
        if (!productName) return null;
        const lowerName = productName.toLowerCase();
        for (const [variantId, meta] of this.flavorMeta.entries()) {
          if (meta.title.toLowerCase() === lowerName) return variantId;
        }
        return null;
      }

      markVariantUnavailable(variantId) {
        const meta = this.flavorMeta.get(String(variantId));
        if (meta) {
          meta.inventoryLimited = true;
          meta.maxAvailable = 0;
        }

        for (const box of this.boxes) {
          delete box[variantId];
        }

        const row = this.flavorRows.find((r) => r.dataset.variantId === String(variantId));
        if (row) {
          row.dataset.available = 'false';
          row.dataset.inventoryLimited = 'true';
          row.dataset.maxAvailable = '0';
          row.classList.add('box-builder__flavor--unavailable');
          row.classList.remove('box-builder__flavor--has-qty');

          const controls = row.querySelector('.box-builder__flavor-controls');
          if (controls) {
            controls.outerHTML = '<span class="box-builder__flavor-status">Agotado</span>';
          }

          const stockLabel = row.querySelector('.box-builder__flavor-stock');
          if (stockLabel) {
            stockLabel.textContent = 'Sin stock disponible';
            stockLabel.className = 'box-builder__flavor-stock box-builder__flavor-stock--sold-out';
          }
        }
      }

      bindEvents() {
        this.querySelectorAll('.box-builder__box-option').forEach((option) => {
          option.addEventListener('click', () => this.selectBoxCount(parseInt(option.dataset.boxes, 10)));
        });

        const backButton = this.querySelector('.box-builder__back');
        if (backButton) {
          backButton.addEventListener('click', () => this.showStep(1));
        }

        this.stepElements.forEach((step) => {
          step.addEventListener('click', () => {
            const stepNum = parseInt(step.dataset.step, 10);
            if (stepNum === 1) this.showStep(1);
          });
        });

        if (this.continueButton) {
          this.continueButton.addEventListener('click', () => this.continueToBuild());
        }

        if (this.clearBoxButton) {
          this.clearBoxButton.addEventListener('click', () => this.clearCurrentBox());
        }

        this.querySelectorAll('.box-builder__qty-btn').forEach((button) => {
          button.addEventListener('click', this.onQuantityChange.bind(this));
        });

        this.querySelectorAll('.box-builder__qty-input').forEach((input) => {
          input.addEventListener('change', this.onQuantityInputCommit.bind(this));
          input.addEventListener('blur', this.onQuantityInputCommit.bind(this));
          input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.onQuantityInputCommit(event);
              input.blur();
            }
          });
        });

        this.addButton.addEventListener('click', this.onAddToCart.bind(this));
      }

      showStep(step) {
        this.stepSelectPanel.classList.toggle('box-builder__panel--active', step === 1);
        this.stepBuildPanel.classList.toggle('box-builder__panel--active', step === 2);

        this.stepElements.forEach((element) => {
          const stepNumber = parseInt(element.dataset.step, 10);
          element.classList.toggle('box-builder__step--active', stepNumber === step);
          element.classList.toggle('box-builder__step--done', stepNumber < step);
          if (stepNumber === step) element.setAttribute('aria-current', 'step');
          else element.removeAttribute('aria-current');
        });

        const divider = this.querySelector('.box-builder__step-divider');
        if (divider) {
          divider.classList.toggle('box-builder__step-divider--done', step > 1);
        }

        if (step === 1) {
          this.updateSelectionStep();
        }

        this.scrollToPanel(step);
      }

      scrollToPanel(step) {
        const target = step === 2 ? this.stepBuildPanel : this.stepSelectPanel;
        if (!target) return;
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }

      scrollToProgress() {
        const progress = this.querySelector('.box-builder__progress');
        if (!progress) return;
        requestAnimationFrame(() => {
          progress.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        });
      }

      applyModeUI() {
        const isSimple = this.simpleMode;
        this.classList.toggle('box-builder--simple', isSimple);
        if (this.buildHeaderEl) {
          this.buildHeaderEl.hidden = isSimple;
        }
      }

      distributeSimplePool() {
        const pool = this.boxes[0] || {};
        const count = this.selectedBoxCount;
        if (count <= 1) return;

        const newBoxes = Array.from({ length: count }, () => ({}));
        const boxUnitCounts = Array.from({ length: count }, () => 0);
        const entries = Object.entries(pool).filter(([, qty]) => qty > 0);

        entries.forEach(([variantId, totalQty]) => {
          for (let unitIndex = 0; unitIndex < totalQty; unitIndex++) {
            let targetBoxIndex = -1;

            for (let i = 0; i < count; i++) {
              if (boxUnitCounts[i] >= this.unitsPerBox) continue;

              if (targetBoxIndex === -1) {
                targetBoxIndex = i;
                continue;
              }

              const currentVariantQty = newBoxes[i][variantId] || 0;
              const targetVariantQty = newBoxes[targetBoxIndex][variantId] || 0;

              if (currentVariantQty < targetVariantQty) {
                targetBoxIndex = i;
                continue;
              }

              if (currentVariantQty === targetVariantQty && boxUnitCounts[i] < boxUnitCounts[targetBoxIndex]) {
                targetBoxIndex = i;
              }
            }

            if (targetBoxIndex === -1) {
              throw new Error('No se pudo distribuir los sabores en cajas completas.');
            }

            newBoxes[targetBoxIndex][variantId] = (newBoxes[targetBoxIndex][variantId] || 0) + 1;
            boxUnitCounts[targetBoxIndex] += 1;
          }
        });

        this.boxes = newBoxes;
        this.activeBoxIndex = 0;
      }

      selectBoxCount(count) {
        this.pendingBoxCount = count;

        this.querySelectorAll('.box-builder__box-option').forEach((option) => {
          const isSelected = parseInt(option.dataset.boxes, 10) === count;
          option.classList.toggle('box-builder__box-option--selected', isSelected);
          option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.updateSelectionStep();
        this.hideError();
      }

      updateSelectionStep() {
        if (!this.selectionSummaryEl || !this.continueButton) return;

        if (!this.pendingBoxCount) {
          this.selectionSummaryEl.textContent = 'Elige cuantas cajas quieres para ver tu total y continuar.';
          this.continueButton.disabled = true;
          this.continueButton.textContent = 'Continuar';
          return;
        }

        const tier = this.getTierForBoxCount(this.pendingBoxCount);
        const totalUnits = this.pendingBoxCount * this.unitsPerBox;
        const totalPrice = tier ? totalUnits * tier.pricePerUnit : 0;

        this.selectionSummaryEl.textContent = `Vas a armar ${this.formatBoxCount(this.pendingBoxCount)} con ${this.formatUnitCount(totalUnits)}${tier ? `, total estimado ${this.formatMoney(totalPrice)}` : ''}.`;
        this.continueButton.disabled = false;
        this.continueButton.textContent = `Continuar con ${this.formatBoxCount(this.pendingBoxCount)}`;
      }

      continueToBuild() {
        if (!this.pendingBoxCount) return;

        const shouldReset = this.selectedBoxCount !== this.pendingBoxCount || this.boxes.length !== this.pendingBoxCount;

        this.selectedBoxCount = this.pendingBoxCount;

        if (shouldReset) {
          this.boxes = Array.from({ length: this.selectedBoxCount }, () => ({}));
          this.activeBoxIndex = 0;
        }

        this.simpleMode = true;
        this.applyModeUI();
        this.renderBoxTabs();
        this.updateBuildPanel();
        this.showStep(2);
      }

      renderBoxTabs() {
        if (!this.boxTabsContainer) return;

        if (!this.selectedBoxCount) {
          this.boxTabsContainer.innerHTML = '';
          return;
        }

        const controlledPanel = this.stepBuildPanel.id || '';
        let html = '';

        for (let index = 0; index < this.selectedBoxCount; index++) {
          const isActive = index === this.activeBoxIndex;
          const isComplete = this.isBoxComplete(index);
          const units = this.getBoxUnits(index);

          html += `
            <button
              type="button"
              role="tab"
              aria-selected="${isActive ? 'true' : 'false'}"
              aria-controls="${controlledPanel}"
              tabindex="${isActive ? '0' : '-1'}"
              class="box-builder__box-tab${isActive ? ' box-builder__box-tab--active' : ''}${isComplete ? ' box-builder__box-tab--complete' : ''}"
              data-box-index="${index}"
            >
              <span class="box-builder__box-tab-label">Caja ${index + 1}</span>
              <span class="box-builder__box-tab-status">${isComplete ? 'Completa' : `${units}/${this.unitsPerBox}`}</span>
            </button>
          `;
        }

        this.boxTabsContainer.innerHTML = html;

        this.boxTabsContainer.querySelectorAll('.box-builder__box-tab').forEach((tab) => {
          tab.addEventListener('click', () => {
            this.activeBoxIndex = parseInt(tab.dataset.boxIndex, 10);
            this.renderBoxTabs();
            this.updateBuildPanel();
            this.scrollToProgress();
          });

          tab.addEventListener('keydown', (event) => this.onBoxTabKeydown(event));
        });
      }

      onBoxTabKeydown(event) {
        const tabs = Array.from(this.boxTabsContainer.querySelectorAll('.box-builder__box-tab'));
        const currentIndex = tabs.indexOf(event.currentTarget);
        if (currentIndex === -1) return;

        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = Math.min(currentIndex + 1, tabs.length - 1);
        if (event.key === 'ArrowLeft') nextIndex = Math.max(currentIndex - 1, 0);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        tabs[nextIndex].focus();
        tabs[nextIndex].click();
      }

      clearCurrentBox() {
        if (!this.boxes[this.activeBoxIndex] || this.getBoxUnits(this.activeBoxIndex) === 0) return;

        this.boxes[this.activeBoxIndex] = {};
        this.hideError();
        this.renderBoxTabs();
        this.updateBuildPanel();
      }

      onQuantityChange(event) {
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;
        const action = button.dataset.action;

        if (this.simpleMode) {
          this.onQuantityChangeSimple(variantId, action);
        } else {
          this.onQuantityChangeAdvanced(variantId, action);
        }
      }

      onQuantityInputCommit(event) {
        const input = event.currentTarget;
        const variantId = input.dataset.variantId;
        const parsedQty = Number.parseInt(input.value, 10);
        const targetQty = Number.isNaN(parsedQty) ? 0 : parsedQty;

        if (this.simpleMode) {
          this.setQuantitySimpleFromInput(variantId, targetQty);
        } else {
          this.setQuantityAdvancedFromInput(variantId, targetQty);
        }
      }

      setQuantitySimpleFromInput(variantId, targetQty) {
        const pool = this.boxes[0];
        if (!pool) return;

        const normalizedQty = Math.max(targetQty, 0);
        const totalUnits = this.getUnitsForBox(pool);
        const totalRequired = this.requiredUnitsAllBoxes;
        const currentQty = pool[variantId] || 0;
        const variantHeadroom = Math.max(totalRequired - (totalUnits - currentQty), 0);
        const remainingAvailability = this.getRemainingVariantAvailability(variantId);
        const variantAvailability = Number.isFinite(remainingAvailability)
          ? Math.max(remainingAvailability + currentQty, 0)
          : Number.POSITIVE_INFINITY;
        const maxAllowedQty = Math.min(variantHeadroom, variantAvailability);
        const clampedQty = Math.min(normalizedQty, maxAllowedQty);

        if (normalizedQty > maxAllowedQty) {
          if (variantHeadroom <= 0) {
            this.showValidation(`Ya tienes ${this.formatUnitCount(totalRequired)} necesarios para ${this.getOwnedBoxCount(this.selectedBoxCount)}.`);
          } else {
            this.showValidation(this.getVariantAvailabilityMessage(variantId));
          }
        }

        if (clampedQty > 0) {
          pool[variantId] = clampedQty;
        } else {
          delete pool[variantId];
        }

        this.hideError();
        this.updateBuildPanel();
      }

      setQuantityAdvancedFromInput(variantId, targetQty) {
        const box = this.boxes[this.activeBoxIndex];
        if (!box) return;

        const normalizedQty = Math.max(targetQty, 0);
        const currentBoxUnits = this.getBoxUnits(this.activeBoxIndex);
        const currentQty = box[variantId] || 0;
        const variantHeadroom = Math.max(this.unitsPerBox - (currentBoxUnits - currentQty), 0);
        const remainingAvailability = this.getRemainingVariantAvailability(variantId);
        const variantAvailability = Number.isFinite(remainingAvailability)
          ? Math.max(remainingAvailability + currentQty, 0)
          : Number.POSITIVE_INFINITY;
        const maxAllowedQty = Math.min(variantHeadroom, variantAvailability);
        const clampedQty = Math.min(normalizedQty, maxAllowedQty);

        if (normalizedQty > maxAllowedQty) {
          if (variantHeadroom <= 0) {
            this.showValidation(`Esta caja ya tiene ${this.formatUnitCount(this.unitsPerBox)}.`);
          } else {
            this.showValidation(this.getVariantAvailabilityMessage(variantId));
          }
        }

        if (clampedQty > 0) {
          box[variantId] = clampedQty;
        } else {
          delete box[variantId];
        }

        this.hideError();
        this.renderBoxTabs();
        this.updateBuildPanel();
      }

      onQuantityChangeSimple(variantId, action) {
        const pool = this.boxes[0];
        if (!pool) return;

        const totalUnits = this.getUnitsForBox(pool);
        const totalRequired = this.requiredUnitsAllBoxes;
        const currentQty = pool[variantId] || 0;

        if (action === 'plus') {
          if (totalUnits >= totalRequired) {
            this.showValidation(`Ya tienes ${this.formatUnitCount(totalRequired)} necesarios para ${this.getOwnedBoxCount(this.selectedBoxCount)}.`);
            return;
          }

          if (this.getRemainingVariantAvailability(variantId) <= 0) {
            this.showValidation(this.getVariantAvailabilityMessage(variantId));
            return;
          }

          pool[variantId] = currentQty + 1;
        } else {
          if (currentQty <= 0) return;
          pool[variantId] = currentQty - 1;
          if (pool[variantId] === 0) delete pool[variantId];
        }

        this.hideError();
        this.updateBuildPanel();
      }

      onQuantityChangeAdvanced(variantId, action) {
        const box = this.boxes[this.activeBoxIndex];

        if (!box) return;

        const currentBoxUnits = this.getBoxUnits(this.activeBoxIndex);
        const currentQty = box[variantId] || 0;

        if (action === 'plus') {
          if (currentBoxUnits >= this.unitsPerBox) {
            this.showValidation(`Esta caja ya tiene ${this.formatUnitCount(this.unitsPerBox)}.`);
            return;
          }

          if (this.getRemainingVariantAvailability(variantId) <= 0) {
            this.showValidation(this.getVariantAvailabilityMessage(variantId));
            return;
          }

          box[variantId] = currentQty + 1;
        } else {
          if (currentQty <= 0) return;
          box[variantId] = currentQty - 1;
          if (box[variantId] === 0) delete box[variantId];
        }

        this.hideError();
        this.renderBoxTabs();
        this.updateBuildPanel();

        if (this.isBoxComplete(this.activeBoxIndex) && this.activeBoxIndex < this.selectedBoxCount - 1) {
          const nextIncomplete = this.boxes.findIndex((candidateBox, index) => {
            return index > this.activeBoxIndex && !this.isBoxComplete(index) && candidateBox;
          });

          if (nextIncomplete !== -1) {
            clearTimeout(this.autoAdvanceTimer);
            this.autoAdvanceTimer = setTimeout(() => {
              this.activeBoxIndex = nextIncomplete;
              this.renderBoxTabs();
              this.updateBuildPanel();
            }, 220);
          }
        }
      }

      showValidation(message) {
        if (!this.progressHint) return;
        this.progressHint.textContent = message;
        this.progressHint.className = 'box-builder__progress-hint box-builder__progress-hint--warning';
        clearTimeout(this.validationTimer);
        this.validationTimer = setTimeout(() => this.updateProgressHint(), 2500);
      }


      updateBuildPanel() {
        if (!this.selectedBoxCount) return;

        if (this.simpleMode) {
          this.updateBuildPanelSimple();
        } else {
          this.updateBuildPanelAdvanced();
        }
      }

      updateBuildPanelSimple() {
        const pool = this.boxes[0] || {};
        const totalUnits = this.getUnitsForBox(pool);
        const totalRequired = this.requiredUnitsAllBoxes;
        const percent = (totalUnits / totalRequired) * 100;
        const isComplete = totalUnits === totalRequired;

        this.querySelectorAll('.box-builder__qty-value').forEach((element) => {
          const variantId = element.dataset.variantId;
          const quantity = pool[variantId] || 0;
          if (element.tagName === 'INPUT') {
            element.value = quantity;
          } else {
            element.textContent = quantity;
          }
          element.setAttribute('aria-label', `Cantidad de ${this.getVariantMeta(variantId).title}: ${quantity}`);
        });

        this.querySelectorAll('.box-builder__flavor').forEach((row) => {
          const variantId = row.dataset.variantId;
          const quantity = pool[variantId] || 0;
          const stockLabel = row.querySelector('.box-builder__flavor-stock');
          const maxAvailable = this.getVariantMaxAvailable(variantId);

          row.classList.toggle('box-builder__flavor--has-qty', quantity > 0);

          if (stockLabel && maxAvailable !== null) {
            const remainingAvailability = this.getRemainingVariantAvailability(variantId);
            row.classList.toggle('box-builder__flavor--limit-reached', remainingAvailability === 0);
            stockLabel.classList.toggle('box-builder__flavor-stock--warning', remainingAvailability > 0 && remainingAvailability <= 5);
            stockLabel.classList.toggle('box-builder__flavor-stock--sold-out', remainingAvailability === 0);

            if (remainingAvailability === 0) {
              stockLabel.textContent = 'Sin mas stock para esta compra';
            } else if (remainingAvailability === 1) {
              stockLabel.textContent = `Te queda 1 ${this.unitSingular} para esta compra`;
            } else {
              stockLabel.textContent = `Te quedan ${this.formatUnitCount(remainingAvailability)} para esta compra`;
            }
          }
        });

        this.querySelectorAll('.box-builder__qty-btn--minus').forEach((button) => {
          button.disabled = (pool[button.dataset.variantId] || 0) <= 0;
        });

        this.querySelectorAll('.box-builder__qty-btn--plus').forEach((button) => {
          button.disabled = totalUnits >= totalRequired || this.getRemainingVariantAvailability(button.dataset.variantId) <= 0;
        });

        if (this.progressFill) {
          this.progressFill.style.width = `${percent}%`;
          this.progressFill.classList.toggle('box-builder__progress-fill--active', totalUnits > 0);
          this.progressFill.classList.toggle('box-builder__progress-fill--complete', isComplete);
        }

        if (this.progressText) {
          this.progressText.textContent = `Tu pedido (${this.formatBoxCount(this.selectedBoxCount)})`;
        }

        if (this.progressCount) {
          this.progressCount.textContent = `${totalUnits} / ${totalRequired}`;
        }

        if (this.progressPct) {
          this.progressPct.textContent = `${Math.round(percent)}%`;
        }

        if (this.progressTrack) {
          this.progressTrack.classList.toggle('box-builder__progress-track--complete', isComplete);
        }

        this.updateTierMeta();
        this.updateProgressHintSimple(totalUnits, totalRequired);
        this.updateSelectionOverviewSimple(pool, totalUnits, totalRequired);
        this.updateSummarySimple();
        this.updateStickyCta();

        if (this.clearBoxButton) {
          this.clearBoxButton.disabled = totalUnits === 0;
        }

        this.addButton.disabled = !isComplete;
        this.addButtonText.textContent = isComplete
          ? `Agregar ${this.formatBoxCount(this.selectedBoxCount)} al carrito`
          : `Agrega ${this.formatUnitCount(totalRequired - totalUnits)} mas para continuar`;
      }

      updateBuildPanelAdvanced() {
        const boxIndex = this.activeBoxIndex;
        const box = this.boxes[boxIndex] || {};
        const units = this.getBoxUnits(boxIndex);
        const percent = (units / this.unitsPerBox) * 100;

        this.querySelectorAll('.box-builder__qty-value').forEach((element) => {
          const variantId = element.dataset.variantId;
          const quantity = box[variantId] || 0;
          if (element.tagName === 'INPUT') {
            element.value = quantity;
          } else {
            element.textContent = quantity;
          }
          element.setAttribute('aria-label', `Cantidad de ${this.getVariantMeta(variantId).title}: ${quantity}`);
        });

        this.querySelectorAll('.box-builder__flavor').forEach((row) => {
          const variantId = row.dataset.variantId;
          const quantity = box[variantId] || 0;
          const stockLabel = row.querySelector('.box-builder__flavor-stock');
          const maxAvailable = this.getVariantMaxAvailable(variantId);

          row.classList.toggle('box-builder__flavor--has-qty', quantity > 0);

          if (stockLabel && maxAvailable !== null) {
            const remainingAvailability = this.getRemainingVariantAvailability(variantId);
            row.classList.toggle('box-builder__flavor--limit-reached', remainingAvailability === 0);
            stockLabel.classList.toggle('box-builder__flavor-stock--warning', remainingAvailability > 0 && remainingAvailability <= 5);
            stockLabel.classList.toggle('box-builder__flavor-stock--sold-out', remainingAvailability === 0);

            if (remainingAvailability === 0) {
              stockLabel.textContent = 'Sin más stock para esta compra';
            } else if (remainingAvailability === 1) {
              stockLabel.textContent = `Te queda 1 ${this.unitSingular} para esta compra`;
            } else {
              stockLabel.textContent = `Te quedan ${this.formatUnitCount(remainingAvailability)} para esta compra`;
            }
          }
        });

        this.querySelectorAll('.box-builder__qty-btn--minus').forEach((button) => {
          button.disabled = (box[button.dataset.variantId] || 0) <= 0;
        });

        this.querySelectorAll('.box-builder__qty-btn--plus').forEach((button) => {
          button.disabled = units >= this.unitsPerBox || this.getRemainingVariantAvailability(button.dataset.variantId) <= 0;
        });

        if (this.progressFill) {
          this.progressFill.style.width = `${percent}%`;
          this.progressFill.classList.toggle('box-builder__progress-fill--active', units > 0);
          this.progressFill.classList.toggle('box-builder__progress-fill--complete', units === this.unitsPerBox);
        }

        if (this.progressText) {
          this.progressText.textContent = `Caja ${boxIndex + 1}`;
        }

        if (this.progressCount) {
          this.progressCount.textContent = `${units} / ${this.unitsPerBox}`;
        }

        if (this.progressPct) {
          this.progressPct.textContent = `${Math.round(percent)}%`;
        }

        if (this.progressTrack) {
          this.progressTrack.classList.toggle('box-builder__progress-track--complete', units === this.unitsPerBox);
        }

        this.updateTierMeta();
        this.updateProgressHint();
        this.updateSelectionOverview();
        this.updateSummary();
        this.updateStickyCta();

        if (this.clearBoxButton) {
          this.clearBoxButton.disabled = units === 0;
        }

        this.addButton.disabled = !this.allBoxesComplete;
        this.addButtonText.textContent = this.allBoxesComplete
          ? `Agregar ${this.formatBoxCount(this.selectedBoxCount)} al carrito`
          : `Completa ${this.selectedBoxCount === 1 ? 'la caja' : 'las cajas'} para continuar`;
      }

      updateTierMeta() {
        const tier = this.currentTier;
        const totalSelected = this.getWorkingUnitsTotal();
        const totalRequired = this.requiredUnitsAllBoxes;

        if (this.selectedBoxesPill) {
          this.selectedBoxesPill.textContent = this.formatBoxCount(this.selectedBoxCount);
        }

        if (this.unitPricePill) {
          this.unitPricePill.textContent = tier
            ? tier.label || `${this.formatMoney(tier.pricePerUnit)} por ${this.unitSingular}`
            : `Precio por ${this.unitSingular}`;
        }

        if (this.totalUnitsPill) {
          this.totalUnitsPill.textContent = `${totalSelected}/${totalRequired} ${this.unitPlural}`;
        }
      }

      updateSelectionOverview() {
        if (!this.selectionOverviewEl || !this.selectedBoxCount) return;

        const currentBoxUnits = this.getBoxUnits(this.activeBoxIndex);
        const currentBoxFlavors = this.getFlavorTotals(this.activeBoxIndex);
        const remaining = Math.max(this.unitsPerBox - currentBoxUnits, 0);

        const flavorChips = currentBoxFlavors.length
          ? currentBoxFlavors
              .map((flavor) => `<span class="box-builder__selection-chip">${flavor.title} x${flavor.quantity}</span>`)
              .join('')
          : '<span class="box-builder__selection-empty">Todavia no agregas sabores a esta caja.</span>';

        this.selectionOverviewEl.hidden = false;
        this.selectionOverviewEl.innerHTML = `
          <div class="box-builder__selection-overview-header">
            <span class="box-builder__selection-overview-title">Tu mezcla en vivo</span>
            <span class="box-builder__selection-overview-meta">Caja ${this.activeBoxIndex + 1}: ${currentBoxUnits}/${this.unitsPerBox}</span>
          </div>
          <div class="box-builder__selection-overview-copy">
            ${remaining === 0 ? 'Tu caja actual ya esta completa.' : `Te faltan ${this.formatUnitCount(remaining)} para completar esta caja.`}
          </div>
          <div class="box-builder__selection-overview-list">${flavorChips}</div>
        `;
      }

      updateStickyCta() {
        if (!this.stickyTotalLabel || !this.stickyTotalMeta || !this.stickyTotalValue) return;

        const tier = this.currentTier;

        if (!tier || !this.selectedBoxCount) {
          this.stickyTotalLabel.textContent = 'Total estimado';
          this.stickyTotalMeta.textContent = 'Elige tus sabores para ver el avance del pedido.';
          this.stickyTotalValue.textContent = '$0';
          return;
        }

        const totalSelected = this.getWorkingUnitsTotal();
        const totalRequired = this.requiredUnitsAllBoxes;
        const remaining = Math.max(totalRequired - totalSelected, 0);
        const estimatedTotal = totalRequired * tier.pricePerUnit;

        this.stickyTotalLabel.textContent = this.allBoxesComplete ? 'Total' : 'Total estimado';
        this.stickyTotalMeta.textContent = this.allBoxesComplete
          ? `${this.selectedBoxCount} ${this.selectedBoxCount === 1 ? 'caja lista' : 'cajas listas'} para agregar al carrito.`
          : `Llevas ${totalSelected}/${totalRequired} ${this.unitPlural}. Faltan ${this.formatUnitCount(remaining)}.`;
        this.stickyTotalValue.textContent = this.formatMoney(estimatedTotal);
      }

      updateProgressHint() {
        if (!this.progressHint) return;

        const units = this.getBoxUnits(this.activeBoxIndex);
        const remaining = this.unitsPerBox - units;

        if (units === 0) {
          this.progressHint.textContent = `Agrega ${this.formatUnitCount(this.unitsPerBox)} para completar esta caja. Puedes mezclar sabores libremente.`;
          this.progressHint.className = 'box-builder__progress-hint';
        } else if (units === this.unitsPerBox) {
          this.progressHint.textContent = 'Caja completa. Ya puedes seguir con la siguiente o agregar tu pedido al carrito.';
          this.progressHint.className = 'box-builder__progress-hint box-builder__progress-hint--success';
        } else {
          this.progressHint.textContent = `Te faltan ${this.formatUnitCount(remaining)} para completar esta caja.`;
          this.progressHint.className = 'box-builder__progress-hint';
        }
      }

      updateSummary() {
        const tier = this.currentTier;

        if (!tier || !this.selectedBoxCount) {
          this.summaryEl.hidden = true;
          return;
        }

        const totalSelected = this.totalUnitsAllBoxes;
        const totalRequired = this.requiredUnitsAllBoxes;
        const remaining = Math.max(totalRequired - totalSelected, 0);
        const unitPrice = tier.pricePerUnit;
        const estimatedTotal = totalRequired * unitPrice;
        const currentSubtotal = totalSelected * unitPrice;
        const baseTier = this.tiers[0];
        const savings = baseTier ? Math.max(totalRequired * (baseTier.pricePerUnit - unitPrice), 0) : 0;
        const activeBoxFlavors = this.getFlavorTotals(this.activeBoxIndex);
        const orderFlavors = this.getFlavorTotals();

        let boxLines = '';
        for (let index = 0; index < this.selectedBoxCount; index++) {
          const boxUnits = this.getBoxUnits(index);
          boxLines += `
            <div class="box-builder__summary-line">
              <span>Caja ${index + 1}</span>
              <span>${boxUnits}/${this.unitsPerBox} ${this.unitPlural}</span>
            </div>
          `;
        }

        const activeBoxFlavorLines = activeBoxFlavors.length
          ? activeBoxFlavors
              .map((flavor) => `<span class="box-builder__summary-chip">${flavor.title} x${flavor.quantity}</span>`)
              .join('')
          : '<span class="box-builder__summary-empty">Todavia no agregas sabores en la caja actual.</span>';

        const orderFlavorLines = orderFlavors.length
          ? orderFlavors
              .map((flavor) => `<span class="box-builder__summary-chip">${flavor.title} x${flavor.quantity}</span>`)
              .join('')
          : '<span class="box-builder__summary-empty">Empieza a sumar sabores para ver el resumen de tu pedido.</span>';

        this.summaryEl.hidden = false;
        this.summaryEl.innerHTML = `
          <div class="box-builder__summary-hero">
            <div>
              <div class="box-builder__summary-heading">Tu pedido hasta ahora</div>
              <div class="box-builder__summary-total">${this.formatMoney(estimatedTotal)}</div>
            </div>
            <div class="box-builder__summary-status">${remaining === 0 ? 'Pedido listo' : `Faltan ${remaining}`}</div>
          </div>
          <div class="box-builder__summary-meta">
            <span>Llevas ${totalSelected} de ${this.formatUnitCount(totalRequired)}</span>
            <span>${remaining === 0 ? 'Listo para agregar al carrito' : `Faltan ${this.formatUnitCount(remaining)}`}</span>
          </div>
          <div class="box-builder__summary-line">
            <span>Precio por ${this.unitSingular}</span>
            <span>${this.formatMoney(unitPrice)}</span>
          </div>
          <div class="box-builder__summary-line">
            <span>${this.allBoxesComplete ? 'Total' : 'Total estimado'}</span>
            <span>${this.formatMoney(estimatedTotal)}</span>
          </div>
          ${!this.allBoxesComplete ? `
            <div class="box-builder__summary-line box-builder__summary-line--muted">
              <span>Acumulado hasta ahora</span>
              <span>${this.formatMoney(currentSubtotal)}</span>
            </div>
          ` : ''}
          <div class="box-builder__summary-section">
            <div class="box-builder__summary-section-title">Caja ${this.activeBoxIndex + 1}</div>
            <div class="box-builder__summary-flavor-list">${activeBoxFlavorLines}</div>
          </div>
          <div class="box-builder__summary-section">
            <div class="box-builder__summary-section-title">Tu mezcla total</div>
            <div class="box-builder__summary-flavor-list">${orderFlavorLines}</div>
          </div>
          <div class="box-builder__summary-breakdown">${boxLines}</div>
          ${savings > 0 ? `<div class="box-builder__summary-savings">${this.allBoxesComplete ? 'Ahorras' : 'Ahorro proyectado'} ${this.formatMoney(savings)}</div>` : ''}
        `;
      }

      updateProgressHintSimple(totalUnits, totalRequired) {
        if (!this.progressHint) return;

        const remaining = totalRequired - totalUnits;

        if (totalUnits === 0) {
          this.progressHint.textContent = `Agrega ${this.formatUnitCount(totalRequired)} para completar tu pedido. Tu seleccion se repartira en ${this.getOwnedBoxCount(this.selectedBoxCount)}.`;
          this.progressHint.className = 'box-builder__progress-hint';
        } else if (remaining <= 0) {
          this.progressHint.textContent = `Pedido completo. Tu seleccion se repartira equitativamente en ${this.getOwnedBoxCount(this.selectedBoxCount)}.`;
          this.progressHint.className = 'box-builder__progress-hint box-builder__progress-hint--success';
        } else {
          this.progressHint.textContent = `Te faltan ${this.formatUnitCount(remaining)}. Tu seleccion se repartira en ${this.formatBoxCount(this.selectedBoxCount)}.`;
          this.progressHint.className = 'box-builder__progress-hint';
        }
      }

      updateSelectionOverviewSimple(pool, totalUnits, totalRequired) {
        if (!this.selectionOverviewEl) return;

        const flavors = this.getFlavorTotals(0);
        const remaining = Math.max(totalRequired - totalUnits, 0);

        const flavorChips = flavors.length
          ? flavors
              .map((flavor) => `<span class="box-builder__selection-chip">${flavor.title} x${flavor.quantity}</span>`)
              .join('')
          : '<span class="box-builder__selection-empty">Todavia no agregas sabores.</span>';

        this.selectionOverviewEl.hidden = false;
        this.selectionOverviewEl.innerHTML = `
          <div class="box-builder__selection-overview-header">
            <span class="box-builder__selection-overview-title">Tu mezcla</span>
            <span class="box-builder__selection-overview-meta">${totalUnits}/${totalRequired} ${this.unitPlural}</span>
          </div>
          <div class="box-builder__selection-overview-copy">
            ${remaining === 0
              ? `Listo. Se repartiran equitativamente en ${this.getOwnedBoxCount(this.selectedBoxCount)}.`
              : `Agrega ${this.formatUnitCount(remaining)} mas. Tu seleccion se repartira en ${this.formatBoxCount(this.selectedBoxCount)}.`}
          </div>
          <div class="box-builder__selection-overview-list">${flavorChips}</div>
        `;
      }

      updateSummarySimple() {
        const tier = this.currentTier;

        if (!tier || !this.selectedBoxCount) {
          this.summaryEl.hidden = true;
          return;
        }

        const pool = this.boxes[0] || {};
        const totalUnits = this.getUnitsForBox(pool);
        const totalRequired = this.requiredUnitsAllBoxes;
        const remaining = Math.max(totalRequired - totalUnits, 0);
        const unitPrice = tier.pricePerUnit;
        const estimatedTotal = totalRequired * unitPrice;
        const baseTier = this.tiers[0];
        const savings = baseTier ? Math.max(totalRequired * (baseTier.pricePerUnit - unitPrice), 0) : 0;
        const flavors = this.getFlavorTotals(0);
        const isComplete = remaining === 0;

        const flavorLines = flavors.length
          ? flavors
              .map((flavor) => `<span class="box-builder__summary-chip">${flavor.title} x${flavor.quantity}</span>`)
              .join('')
          : '<span class="box-builder__summary-empty">Agrega sabores para ver el resumen.</span>';

        this.summaryEl.hidden = false;
        this.summaryEl.innerHTML = `
          <div class="box-builder__summary-hero">
            <div>
              <div class="box-builder__summary-heading">Tu pedido</div>
              <div class="box-builder__summary-total">${this.formatMoney(estimatedTotal)}</div>
            </div>
            <div class="box-builder__summary-status">${isComplete ? 'Pedido listo' : `Faltan ${remaining}`}</div>
          </div>
          <div class="box-builder__summary-meta">
            <span>${totalUnits} de ${this.formatUnitCount(totalRequired)}</span>
            <span>${this.formatBoxCount(this.selectedBoxCount)} &middot; distribucion automatica</span>
          </div>
          <div class="box-builder__summary-line">
            <span>Precio por ${this.unitSingular}</span>
            <span>${this.formatMoney(unitPrice)}</span>
          </div>
          <div class="box-builder__summary-line">
            <span>${isComplete ? 'Total' : 'Total estimado'}</span>
            <span>${this.formatMoney(estimatedTotal)}</span>
          </div>
          <div class="box-builder__summary-section">
            <div class="box-builder__summary-section-title">Tu mezcla</div>
            <div class="box-builder__summary-flavor-list">${flavorLines}</div>
          </div>
          ${savings > 0 ? `<div class="box-builder__summary-savings">${isComplete ? 'Ahorras' : 'Ahorro proyectado'} ${this.formatMoney(savings)}</div>` : ''}
        `;
      }

      formatMoney(amount) {
        const locale = document.documentElement.lang || 'es-CO';
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(amount);
        } catch {
          return `$${amount.toLocaleString()}`;
        }
      }

      getCartErrorMessage(data) {
        if (typeof data?.description === 'string' && data.description.trim()) return data.description;
        if (typeof data?.message === 'string' && data.message.trim()) return data.message;

        if (data?.errors) {
          if (typeof data.errors === 'string' && data.errors.trim()) return data.errors;

          if (typeof data.errors === 'object') {
            const firstError = Object.values(data.errors).find((value) => {
              if (Array.isArray(value)) return value.length > 0;
              return Boolean(value);
            });

            if (Array.isArray(firstError) && firstError[0]) return firstError[0];
            if (typeof firstError === 'string') return firstError;
          }
        }

        return 'No se pudo agregar la caja al carrito.';
      }

      async parseCartResponse(response) {
        const raw = await response.text();

        if (!raw) {
          return {
            status: response.ok ? 0 : response.status,
            message: response.ok ? '' : `Error HTTP ${response.status}`,
          };
        }

        try {
          const data = JSON.parse(raw);
          if (!response.ok && !data.status) data.status = response.status;
          return data;
        } catch {
          return {
            status: response.ok ? 0 : response.status,
            message: raw,
          };
        }
      }

      renderBundleCartNotification(parsedState) {
        if (!this.cart || !this.cart.getSectionsToRender || !this.cart.open) return false;

        const sections = parsedState.sections || {};

        this.cart.getSectionsToRender().forEach((section) => {
          if (section.id === 'cart-notification-product') return;

          const target = document.getElementById(section.id);
          const html = sections[section.id];

          if (!target || !html) return;

          target.innerHTML = this.cart.getSectionInnerHTML(html, section.selector);
        });

        const tier = this.currentTier;
        const totalPrice = tier ? this.totalUnitsAllBoxes * tier.pricePerUnit : 0;
        const flavorIds = this.boxes.reduce((ids, box) => ids.concat(Object.keys(box)), []);
        const flavorCount = new Set(flavorIds).size;
        const productContainer = document.getElementById('cart-notification-product');
        const bundleDisplayName = this.getBundleDisplayName(this.selectedBoxCount);
        const addedLabel = this.selectedBoxCount === 1 ? 'agregada' : 'agregadas';

        if (productContainer) {
          productContainer.innerHTML = `
            <div class="cart-item">
              <div>
                <h3 class="cart-notification-product__name h4">${bundleDisplayName} ${addedLabel}</h3>
                <dl>
                  <div class="product-option">
                    <dt>Cajas:</dt>
                    <dd>${this.selectedBoxCount}</dd>
                  </div>
                  <div class="product-option">
                    <dt>${this.capitalizeLabel(this.unitPlural)}:</dt>
                    <dd>${this.totalUnitsAllBoxes}</dd>
                  </div>
                  <div class="product-option">
                    <dt>Sabores elegidos:</dt>
                    <dd>${flavorCount}</dd>
                  </div>
                  ${tier ? `
                    <div class="product-option">
                      <dt>Total:</dt>
                      <dd>${this.formatMoney(totalPrice)}</dd>
                    </div>
                  ` : ''}
                </dl>
              </div>
            </div>
          `;
        }

        if (this.cart.header) this.cart.header.reveal();
        this.cart.open();
        return true;
      }

      async getCartItemCount() {
        try {
          const response = await fetch(`${routes.cart_url}.js`);
          if (!response.ok) return null;
          const state = await response.json();
          return Number.isInteger(state.item_count) ? state.item_count : null;
        } catch {
          return null;
        }
      }

      async waitForCartCommit(previousCount, expectedDelta) {
        if (!Number.isInteger(previousCount) || !Number.isInteger(expectedDelta) || expectedDelta <= 0) return;

        const targetCount = previousCount + expectedDelta;
        const maxAttempts = 5;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const currentCount = await this.getCartItemCount();

          if (Number.isInteger(currentCount) && currentCount >= targetCount) {
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
        }
      }

      async renderFreshCartDrawer() {
        if (!this.cart || this.cart.tagName.toLowerCase() !== 'cart-drawer') return;

        const [drawerResponse, bubbleResponse] = await Promise.all([
          fetch(`${routes.cart_url}?section_id=cart-drawer`),
          fetch(`${window.location.pathname}?section_id=cart-icon-bubble`),
        ]);

        if (!drawerResponse.ok || !bubbleResponse.ok) {
          throw new Error('No se pudieron refrescar las secciones del carrito lateral.');
        }

        const [drawerHtml, bubbleHtml] = await Promise.all([drawerResponse.text(), bubbleResponse.text()]);

        if (this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }

        this.cart.renderContents({
          sections: {
            'cart-drawer': drawerHtml,
            'cart-icon-bubble': bubbleHtml,
          },
        });
      }

      async refreshCartNotificationBubble() {
        const bubbleElement = document.getElementById('cart-icon-bubble');
        if (!bubbleElement) return;

        const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
        if (!response.ok) return;

        const html = await response.text();
        const nextMarkup = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-icon-bubble');
        if (nextMarkup) {
          bubbleElement.innerHTML = nextMarkup.innerHTML;
        }
      }

      async syncCartSurfaces() {
        const refreshers = [];
        const drawerItems = document.querySelector('cart-drawer-items');
        const pageCartItems = document.querySelector('cart-items');

        if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
          refreshers.push(drawerItems.onCartUpdate());
        }

        if (pageCartItems && typeof pageCartItems.onCartUpdate === 'function') {
          refreshers.push(pageCartItems.onCartUpdate());
        }

        await Promise.all(refreshers);
      }

      async onAddToCart(event) {
        event.preventDefault();

        if (!this.allBoxesComplete) {
          this.showError('Debes completar todas las cajas antes de agregar al carrito.');
          return;
        }

        if (!this.validateInventoryAvailability()) {
          return;
        }

        this.addButton.disabled = true;
        this.addButton.classList.add('loading');
        this.spinner.classList.remove('hidden');
        this.hideError();

        const savedBoxes = this.simpleMode && this.selectedBoxCount > 1
          ? [Object.assign({}, this.boxes[0])]
          : null;

        if (savedBoxes) {
          this.distributeSimplePool();
        }

        const items = [];
        const boxImagesById = {};
        const previousCartCount = await this.getCartItemCount();

        for (let index = 0; index < this.boxes.length; index++) {
          const box = this.boxes[index];
          const boxId = `b-${Date.now().toString(36)}-${index + 1}`;

          if (this.boxImage) {
            boxImagesById[boxId] = this.boxImage;
          }

          const sortedEntries = Object.entries(box)
            .filter(([, quantity]) => quantity > 0)
            .sort((a, b) => this.getFlavorSortIndex(b[0]) - this.getFlavorSortIndex(a[0]));

          for (const [variantId, quantity] of sortedEntries) {
            const properties = {
              _Caja: boxId,
              _fo: `${this.getFlavorSortIndex(variantId)}`,
            };

            items.push({
              id: parseInt(variantId, 10),
              quantity,
              properties,
            });
          }
        }

        if (items.length === 0) {
          this.showError(`No has seleccionado ${this.unitPlural}.`);
          this.addButton.classList.remove('loading');
          this.spinner.classList.add('hidden');
          return;
        }

        const expectedAddedUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = { items };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        config.body = JSON.stringify(body);

        try {
          const response = await fetch(routes.cart_add_url, config);
          const data = await this.parseCartResponse(response);

          if (data.status) {
            if (savedBoxes) this.boxes = savedBoxes;

            if (this.isSoldOutError(data)) {
              const productName = this.extractSoldOutProductName(data);
              const soldVariantId = this.findVariantByProductName(productName);

              if (soldVariantId) {
                this.markVariantUnavailable(soldVariantId);
                this.updateBuildPanel();
                this.showError(`${productName} ya no está disponible y fue removido de tu selección. Reemplázalo con otro sabor y vuelve a intentar.`);
              } else {
                this.showError(this.getCartErrorMessage(data));
              }
            } else {
              this.showError(this.getCartErrorMessage(data));
            }

            try {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'box-builder',
                errors: data.errors || data.description,
                message: data.message,
              });
            } catch (publishError) {
              console.error('Box Builder cartError publish failed', publishError);
            }
            return;
          }

          this.persistBoxImages(boxImagesById);

          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'box-builder',
              cartData: data,
            });
          } catch (publishError) {
            console.error('Box Builder cartUpdate publish failed', publishError);
          }

          if (this.cart) {
            try {
              const cartTagName = this.cart.tagName.toLowerCase();
              await this.waitForCartCommit(previousCartCount, expectedAddedUnits);

              if (cartTagName === 'cart-drawer') {
                await this.renderFreshCartDrawer();
                await this.syncCartSurfaces();
              } else {
                const usedCustomNotification =
                  cartTagName === 'cart-notification' && Array.isArray(data.items) && !data.key && this.renderBundleCartNotification(data);

                if (!usedCustomNotification) {
                  this.cart.renderContents(data);
                }

                await this.refreshCartNotificationBubble();
              }
            } catch (renderError) {
              console.error('Box Builder cart render failed', renderError);
              window.location = routes.cart_url;
              return;
            }
          } else {
            window.location = routes.cart_url;
            return;
          }

          this.applyBoxImagesInDom(boxImagesById);

          requestAnimationFrame(() => {
            this.applyBoxImagesInDom(boxImagesById);
          });

          setTimeout(() => {
            this.applyBoxImagesInDom(boxImagesById);
            document.dispatchEvent(
              new CustomEvent('boxbuilder:images-updated', {
                detail: { boxImageCount: Object.keys(boxImagesById).length },
              })
            );
          }, 120);

          this.resetBuilder();
        } catch (error) {
          if (savedBoxes) this.boxes = savedBoxes;
          console.error(error);
          this.showError('No se pudo conectar con Shopify. Revisa tu red e intenta de nuevo.');
        } finally {
          this.addButton.classList.remove('loading');
          this.spinner.classList.add('hidden');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.addButton.disabled = !this.allBoxesComplete;
        }
      }

      resetBuilder() {
        this.pendingBoxCount = 0;
        this.selectedBoxCount = 0;
        this.boxes = [];
        this.activeBoxIndex = 0;
        this.simpleMode = true;
        this.classList.remove('box-builder--simple');
        if (this.buildHeaderEl) this.buildHeaderEl.hidden = false;

        this.querySelectorAll('.box-builder__box-option').forEach((option) => {
          option.classList.remove('box-builder__box-option--selected');
          option.setAttribute('aria-pressed', 'false');
        });

        this.renderBoxTabs();
        this.summaryEl.hidden = true;
        this.hideError();
        this.updateSelectionStep();
        this.showStep(1);
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        this.errorEl.hidden = true;
      }
    }
  );
}
